import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Heading } from 'ui/components/1-atoms';
import styles from './Accordion.module.scss';

import {
	AccordionItem,
	AccordionItemDataProps,
} from './AccordionItem';

export interface AccordionProps {
	className?: string;
	allowMultiple?: boolean;
	title?: string;
	backgroundColor?: string;
	textColor?: string;
	accordionItems: AccordionItemDataProps[];
	showOpenCloseAll?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
	className,
	title,
	backgroundColor = 'transparent',
	textColor,
	accordionItems,
	showOpenCloseAll = true,
}) => {
	const itemHeadingLevel = title ? 'h3' : 'h2';

	const [openItems, setOpenItems] = useState<boolean[]>(() =>
		accordionItems.map((item) => item.openByDefault ?? false)
	);

	const allOpen =
		accordionItems.length > 0 && openItems.every((open) => open);

	const toggleItem = (index: number) => {
		setOpenItems((currentOpenItems) =>
			currentOpenItems.map((open, itemIndex) =>
				itemIndex === index ? !open : open
			)
		);
	};

	const toggleAll = () => {
		setOpenItems(accordionItems.map(() => !allOpen));
	};

	useEffect(() => {
		const openItemFromHash = () => {
			const hash = window.location.hash.replace('#', '');

			if (!hash) {
				return;
			}

			const matchingIndex = accordionItems.findIndex(
				(item) => item.anchor === hash
			);

			if (matchingIndex === -1) {
				return;
			}

			setOpenItems(
				accordionItems.map((_, index) => index === matchingIndex)
			);
		};

		openItemFromHash();

		window.addEventListener('hashchange', openItemFromHash);

		return () => {
			window.removeEventListener('hashchange', openItemFromHash);
		};
	}, [accordionItems]);

	return (
		<div
			className={classNames(styles.Accordion, className)}
			style={
				{
					'--accordion-bg-color': backgroundColor,
					'--accordion-text-color': textColor,
				} as React.CSSProperties
			}
		>
			{title && (
				<Heading
					headingLevel="h2"
					style="sm"
					className={classNames(
						styles.Accordion_heading,
						'Accordion_heading'
					)}
				>
					{title}
				</Heading>
			)}

			{showOpenCloseAll && accordionItems.length > 0 && (
				<div
					className={classNames(
						styles.Accordion_toggleAllContainer,
						'u-hidden-in-print'
					)}
				>
					<button
						type="button"
						className={styles.Accordion_toggleAll}
						onClick={toggleAll}
					>
						{allOpen ? 'Luk alle' : 'Åbn alle'}
					</button>
				</div>
			)}

			{accordionItems.map((accordionItem, index) => (
				<AccordionItem
					{...accordionItem}
					headingLevel={itemHeadingLevel}
					textColor={textColor}
					open={openItems[index]}
					onToggle={() => toggleItem(index)}
					key={index}
				/>
			))}
		</div>
	);
};